import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Billable, Event } from '@feathr/blackbox';
import { Button, ButtonValid, Card, Popover } from '@feathr/components';
import { currencyFormatter } from '@feathr/extender/utils';
import { useToggle } from '@feathr/hooks';
import { useRole } from '@feathr/report_components';

import AddAddress from './AddAddress';
import AddFunds from './AddFunds';
import CreateTransactionButton from './CreateTransactionButton';
import TransferFunds from './TransferFunds';

import * as styles from './BillingFunds.css';

interface IProps {
  event: Event;
  billable?: Billable;
}

function BillingFunds({ event, billable }: Readonly<IProps>): JSX.Element | null {
  const billing = event.get('billing');
  const { hasTransactions } = useRole();
  const [showTransferFundsModal, setShowTransferFundsModal] = useState(false);
  const [showAddFundsModal, toggleShowAddFundsModal] = useToggle(false);
  const [showAddAddressModal, toggleShowAddAddressModal] = useToggle(false);
  const { t } = useTranslation();

  // Billable is required
  if (!billable || billable.isPending) {
    return null;
  }

  function toggleShowTransferFundsModal(): void {
    setShowTransferFundsModal(!showTransferFundsModal);
  }

  const hasFunds: boolean = (billing?.max_transfer || 0) > 0;

  function validateFunds(): string[] {
    return !hasFunds ? [t('No pre-paid funds available to transfer.')] : [];
  }

  const mediaCreditBalance = currencyFormatter.format(
    Math.max(billing?.media_credit_balance || 0, 0),
  );

  const maxTransfer = currencyFormatter.format(Math.max(billing?.max_transfer || 0, 0));
  const transferDiff = currencyFormatter.format(
    Math.max(
      Math.max(billing?.media_credit_balance || 0) - Math.max(billing?.max_transfer || 0),
      0,
    ),
  );

  return (
    <>
      <Card className={styles.card} contentClassName={styles.cardContent}>
        <div className={styles.funds}>
          <label>
            {t('Media credit balance')}
            <Popover toggle={'onMouseOver'}>
              <span>
                <FontAwesomeIcon className={styles.info} icon={faInfoCircle} />
              </span>
              <section className={styles.section}>
                <p>{t('The amount you have currently funded on this project.')}</p>
              </section>
              <section>
                {hasFunds && (
                  <p>
                    {t(
                      "On the 5th of each month, funds will be automatically pulled from this balance to pay the billing period's media invoice.",
                    )}
                  </p>
                )}
              </section>
            </Popover>
          </label>
          <span>{mediaCreditBalance}</span>
        </div>
        <div className={styles.funds}>
          <label>
            {t('Available to transfer')}
            <Popover toggle={'onMouseOver'}>
              <span>
                <FontAwesomeIcon className={styles.info} icon={faInfoCircle} />
              </span>
              <section className={styles.section}>
                <p>{t('The amount you have available to transfer from this project.')}</p>
                {mediaCreditBalance !== maxTransfer && (
                  <div>
                    <p>
                      {t(
                        'Amount available to transfer is reduced by budgeted spend of {{transferDiff}}',
                        { transferDiff },
                      )}
                    </p>
                  </div>
                )}
              </section>
            </Popover>
          </label>
          <span>{maxTransfer}</span>
        </div>
        <div className={styles.buttons}>
          <ButtonValid
            errors={validateFunds()}
            isFullWidth={true}
            onClick={toggleShowTransferFundsModal}
          >
            {t('Transfer pre-paid funds')}
          </ButtonValid>
          <Button
            disabled={true}
            isFullWidth={true}
            tooltip={t('Contact accounting@feathr.co to add pre-paid funds.')}
            type={'primary'}
          >
            {t('Add pre-paid funds')}
          </Button>
          {hasTransactions && <CreateTransactionButton event={event} isFullWidth={true} />}
        </div>
      </Card>
      {showTransferFundsModal && (
        <TransferFunds event={event} toggleShowTransferFundsModal={toggleShowTransferFundsModal} />
      )}
      {showAddAddressModal && billable && (
        <AddAddress
          billable={billable}
          event={event}
          toggleShowAddAddressModal={toggleShowAddAddressModal}
        />
      )}
      {showAddFundsModal && billable && (
        <AddFunds
          billable={billable}
          event={event}
          toggleShowAddFundsModal={toggleShowAddFundsModal}
        />
      )}
    </>
  );
}

export default observer(BillingFunds);
